import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, Tabs, Tab, Chip, Skeleton, Alert,
  TextField, InputAdornment, IconButton, Button, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';

import { fmtAUDCompact } from './KpiCard';
import { useThemeColors } from '../theme/ThemeModeContext';
import AlertsWorkspaceTabs from './AlertsWorkspaceTabs';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';
const ACK_LS_KEY = 'veraxis.alerts.acked.v1';

const SEV_META = {
  critical: { color: '#ef4444', icon: ErrorOutlineIcon, label: 'CRITICAL' },
  warning:  { color: '#f59e0b', icon: WarningAmberIcon,  label: 'WARNING' },
  info:     { color: '#3b82f6', icon: InfoOutlinedIcon,  label: 'INFO' },
};

const TABS = [
  { value: 'all',          label: 'All' },
  { value: 'performance',  label: 'Performance' },
  { value: 'commercial',   label: 'Commercial' },
  { value: 'operational',  label: 'Operational' },
  { value: 'compliance',   label: 'Compliance' },
];

function loadAcked() {
  try {
    return new Set(JSON.parse(localStorage.getItem(ACK_LS_KEY) || '[]'));
  } catch { return new Set(); }
}
function persistAcked(set) {
  localStorage.setItem(ACK_LS_KEY, JSON.stringify([...set]));
}

export default function AlertsCentre() {
  const colors = useThemeColors();
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acked, setAcked] = useState(loadAcked);
  const [showAcked, setShowAcked] = useState(false);

  // Server-driven filtering. Category, severity, and free-text search all
  // happen on the backend (modules/portfolio/alerts.py — `?category=`,
  // `?severity=`, `?q=`). Refetch when any of these change so the response
  // is the source of truth. The only JS-side filter that remains is the
  // "showAcked" toggle which depends on localStorage-resident ack state.
  async function load() {
    setLoading(true); setError(null);
    try {
      const params = { period: '7d' };
      if (tab && tab !== 'all') params.category = tab;
      if (query.trim())         params.q = query.trim();
      const r = await axios.get(`${API_BASE_URL}/api/portfolios/alerts`, { params });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }
  // Debounce query → load so we don't fire on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { load(); }, query ? 250 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [tab, query]);

  function ack(alertId) {
    const next = new Set(acked); next.add(alertId);
    setAcked(next); persistAcked(next);
  }
  function unack(alertId) {
    const next = new Set(acked); next.delete(alertId);
    setAcked(next); persistAcked(next);
  }

  // `counts` always reflects the unfiltered set (server keeps it stable as
  // the user filters) — so tab badges don't dance around as you type.
  const counts = data?.counts || { all: 0 };

  // The only JS-side filter left: hide acknowledged rows unless the user
  // explicitly toggled them on. Ack state is local.
  const filtered = useMemo(() => {
    if (!data?.alerts) return [];
    return showAcked
      ? data.alerts
      : data.alerts.filter((a) => !acked.has(a.id));
  }, [data, acked, showAcked]);

  return (
    <Box sx={{ pb: 4 }}>
      <AlertsWorkspaceTabs />
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h5" sx={{ color: 'text.primary' }}>Alerts</Typography>
          <Chip size="small" variant="outlined"
                label={`${counts.all || 0} active`} />
          <Chip size="small"
                sx={{ bgcolor: 'rgba(239,68,68,0.12)', color: '#fca5a5' }}
                label={`${data?.alerts?.filter(a => a.severity === 'critical' && !acked.has(a.id)).length || 0} critical`} />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextField
            size="small"
            placeholder="Search alerts…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
            }}
            sx={{ width: 240 }}
          />
          <Tooltip title={showAcked ? 'Hide acknowledged' : 'Show acknowledged'}>
            <Button size="small"
                    variant={showAcked ? 'contained' : 'outlined'}
                    onClick={() => setShowAcked(!showAcked)}>
              Acked ({acked.size})
            </Button>
          </Tooltip>
          <IconButton size="small" onClick={load} sx={{ color: 'text.secondary' }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Tabs */}
      <Card sx={{ p: 0, mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          {TABS.map(t => (
            <Tab key={t.value} value={t.value}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{t.label}</span>
                  <Chip size="small" variant="outlined"
                    label={counts[t.value] ?? 0}
                    sx={{ height: 18, fontSize: '0.68rem' }}
                  />
                </Stack>
              }
              sx={{ minHeight: 44 }}
            />
          ))}
        </Tabs>
      </Card>

      {/* Alerts list */}
      <Stack spacing={1.25}>
        {loading && [...Array(4)].map((_, i) => (
          <Card key={i} sx={{ p: 2 }}>
            <Skeleton width="60%" sx={{ bgcolor: colors.skeleton }} />
            <Skeleton width="40%" sx={{ bgcolor: colors.skeleton }} />
          </Card>
        ))}
        {!loading && filtered.length === 0 && (
          <Card sx={{ p: 5, textAlign: 'center', color: 'text.disabled' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 40, mb: 1, color: 'success.main' }} />
            <Typography>No active alerts in this view.</Typography>
            <Typography variant="caption">All clear, or acknowledged ones are hidden.</Typography>
          </Card>
        )}
        {!loading && filtered.map(a => {
          const meta = SEV_META[a.severity] || SEV_META.info;
          const isAcked = acked.has(a.id);
          const Icon = meta.icon;
          return (
            <Card key={a.id} sx={{
              p: 2, position: 'relative', overflow: 'hidden',
              opacity: isAcked ? 0.55 : 1,
              borderLeft: '3px solid',
              borderLeftColor: meta.color,
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Icon sx={{ color: meta.color, mt: 0.25 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                      <Typography sx={{
                        fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em',
                        color: meta.color,
                      }}>
                        {meta.label}
                      </Typography>
                      <Chip size="small" variant="outlined" label={a.category} sx={{ height: 18, fontSize: '0.68rem' }} />
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        {a.region} · {a.fuel}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>
                      {a.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.25 }}>
                      {a.message}
                    </Typography>
                    {a.suggested_action && (
                      <Typography sx={{
                        fontSize: '0.78rem', color: 'text.disabled', mt: 0.75, fontStyle: 'italic',
                      }}>
                        Suggested: {a.suggested_action}
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                  {a.aud_impact > 0 && (
                    <Typography sx={{
                      fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                      color: 'text.primary', fontSize: '0.95rem',
                    }}>
                      ~{fmtAUDCompact(a.aud_impact)}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    Est. impact
                  </Typography>
                  {!isAcked ? (
                    <Button size="small" variant="outlined"
                            onClick={() => ack(a.id)}
                            sx={{ mt: 0.5 }}>
                      Acknowledge
                    </Button>
                  ) : (
                    <Button size="small" variant="text"
                            startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                            onClick={() => unack(a.id)}
                            sx={{ mt: 0.5, color: 'success.main' }}>
                      Acked · undo
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Card>
          );
        })}
      </Stack>

      {/* Footer note */}
      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 3 }}>
        Rules engine v1: low availability, offline detection, negative capture.
        Window: last 7 days. Acks stored locally in this browser; persisted ack table lands with the alerts schema.
      </Typography>
    </Box>
  );
}
